import { Button } from 'antd';
import { CheckCircleOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /features/whatsapp - prerendered feature page targeting searches like
 * "WhatsApp school communication" and "school WhatsApp system Zimbabwe".
 */
export const WhatsAppFeaturePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/features/whatsapp">
      <SEO
        title="WhatsApp School Communication for Parents and Teachers | Rongeka Africa"
        description="Run your school on WhatsApp. Parents check fees, results and attendance, teachers mark registers, and drivers get routes - no app to download, no passwords to remember."
        url="https://rongeka.africa/features/whatsapp"
        keywords="WhatsApp school communication, school WhatsApp chatbot, parent communication system Zimbabwe, school messaging system, WhatsApp school portal"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">WHATSAPP FIRST</div>
        <h1 className="mkt-hero-title">Run Your School on WhatsApp</h1>
        <p className="mkt-hero-lead">
          Your parents, teachers, and drivers already have WhatsApp. Rongeka meets them
          there - no app to download, no password to forget, no training needed. One
          message and they are in.
        </p>
        <div className="mkt-hero-actions">
          <Button
            type="primary"
            size="large"
            icon={<WhatsAppOutlined />}
            onClick={() => window.open('https://wa.me/263789442202?text=hi', '_blank')}
          >
            Say Hi on WhatsApp
          </Button>
          <Button size="large" onClick={() => navigate('/get-started')}>
            Get Started
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Why WhatsApp instead of another app?</h2>
          <p className="mkt-section-body">
            School apps fail in Zimbabwe for a simple reason: parents do not download them.
            Data is expensive, phone storage is limited, and passwords get forgotten. WhatsApp
            is the one app almost every parent already opens every day. Rongeka turns it into
            your school's front door, so adoption is immediate instead of a term-long battle.
          </p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">One number, every role</h2>
          <div className="mkt-grid">
            <div className="mkt-card">
              <h3>Parents</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Fee balances, invoices, and statements</li>
                <li><CheckCircleOutlined />Attendance alerts the moment they matter</li>
                <li><CheckCircleOutlined />Student results and report cards</li>
                <li><CheckCircleOutlined />Message the school directly</li>
              </ul>
            </div>
            <div className="mkt-card">
              <h3>Teachers</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Mark attendance from their phone</li>
                <li><CheckCircleOutlined />View their classes and timetable</li>
                <li><CheckCircleOutlined />Access grades and assessments</li>
              </ul>
            </div>
            <div className="mkt-card">
              <h3>Drivers</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />View daily routes and stops</li>
                <li><CheckCircleOutlined />Daily vehicle check-ins</li>
                <li><CheckCircleOutlined />Report issues to the school</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Bulk messaging that parents actually read</h2>
          <p className="mkt-section-body">
            Send fee reminders, results notifications, and announcements to every guardian in
            one go, over WhatsApp and email. Unlike printed circulars that never leave the
            school bag, WhatsApp messages land in the same inbox parents check all day - and
            the system tracks what was delivered.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Automated fee payment reminders</li>
            <li><CheckCircleOutlined />Results and report card notifications</li>
            <li><CheckCircleOutlined />School announcements and event notices</li>
            <li><CheckCircleOutlined />Delivery tracking with failure reasons, so no parent is silently missed</li>
          </ul>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">How it works</h2>
          <ol className="mkt-steps">
            <li>
              <strong>Parent says Hi</strong>
              A guardian sends one WhatsApp message to your school's Rongeka number.
            </li>
            <li>
              <strong>Rongeka recognises them</strong>
              The system matches their number to their children and their role at the school.
            </li>
            <li>
              <strong>They self-serve</strong>
              Balances, results, and attendance are available in seconds, any time - without
              calling the school office.
            </li>
          </ol>
        </div>
      </section>
    </MarketingLayout>
  );
};
